import { Checkbox as PrimeCheckbox } from "primereact/checkbox";
import { InputSwitch as PrimeInputSwitch } from "primereact/inputswitch";
import { RadioButton as PrimeRadioButton } from "primereact/radiobutton";
import { Children, cloneElement, isValidElement } from "react";

import { useFormFieldContext } from "../../form-field.context";
import type { FormFieldControlSlotProps, FormFieldControlViewProps } from "./types-control";

/**
 * Controles do Prime que têm um `<input>` dentro da raiz (identificados pelo tipo do componente do Prime, que o `FormField` precisa importar): o `id` do campo vai para o `inputId` (o `<label for>` aciona o
 * input) e não para a raiz, para não repetir o id no DOM.
 */
const INPUT_ID_CONTROLS: ReadonlySet<unknown> = new Set([
  PrimeInputSwitch,
  PrimeCheckbox,
  PrimeRadioButton,
]);

interface ChildProps {
  inputId?: string;
  className?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
}

/**
 * ViewModel do FormField.Control: clona o ÚNICO filho com as props do campo, mescla `className` e `aria-describedby`
 * do filho com os do campo e só passa `invalid` a componentes (não a elementos DOM). Lança se não houver exatamente
 * um elemento.
 */
export function useFormFieldControl({
  children,
}: FormFieldControlSlotProps): FormFieldControlViewProps {
  const { controlProps, error } = useFormFieldContext();

  if (Children.count(children) !== 1 || !isValidElement<ChildProps>(children)) {
    throw new Error("FormField.Control espera exatamente um filho");
  }
  const own = children.props;
  const describedBy = [own["aria-describedby"], controlProps["aria-describedby"]]
    .filter(Boolean)
    .join(" ");
  const { "aria-labelledby": labelledBy, id: controlId, ...rest } = controlProps;
  const idProps = INPUT_ID_CONTROLS.has(children.type)
    ? { inputId: own.inputId ?? controlId }
    : { id: controlId };

  return {
    element: cloneElement(children, {
      ...rest,
      ...idProps,
      ...(own["aria-labelledby"] ? {} : { "aria-labelledby": labelledBy }),
      ...(describedBy ? { "aria-describedby": describedBy } : {}),
      ...(error && typeof children.type !== "string" ? { invalid: true } : {}),
      ...(error ? { className: [own.className, "p-invalid"].filter(Boolean).join(" ") } : {}),
    }),
  };
}
